import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import HotSidebar from "@/components/feature/HotSidebar";
import { articles, categories, siteInfo } from "@/mocks/news";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((item) => item.slug === slug);
  const [sort, setSort] = useState<"latest" | "hot">("latest");

  const list = useMemo(() => {
    const base = articles.filter((item) => item.categorySlug === slug);
    if (sort === "hot") return [...base].sort((a, b) => b.views - a.views);
    return [...base].sort(
      (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
    );
  }, [slug, sort]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Seo
          title="栏目未找到 | 看瓜爆料"
          description="你访问的栏目不存在或已经调整，返回看瓜爆料首页查看今日速递与实时热榜。"
          keywords="看瓜爆料,栏目不存在,今日速递"
          path={`/category/${slug ?? ""}`}
        />
        <Navbar />
        <main className="mx-auto flex max-w-site flex-col items-center px-4 py-24 text-center md:px-6">
          <i className="ri-question-line text-5xl text-primary-500" aria-hidden="true"></i>
          <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">没有找到这个栏目</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            它可能已经调整了名字。回到首页，从主题分类里重新挑选一个感兴趣的栏目吧。
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" aria-hidden="true"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const pageUrl = `${siteInfo.domain}/category/${category.slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${category.name} - 看瓜爆料`,
      url: pageUrl,
      description: category.desc,
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: list.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${siteInfo.domain}/article/${item.id}`,
          name: item.title,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.domain },
        { "@type": "ListItem", position: 2, name: category.name, item: pageUrl },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title={`${category.name} - 看瓜爆料最新进展 | 实时更新`}
        description={`看瓜爆料${category.name}栏目：${category.desc}今日最新瓜料实时更新，随时刷新随时有料。`}
        keywords={`看瓜爆料,${category.name},看瓜爆料最新,看瓜爆料今日,${category.name}最新进展`}
        path={`/category/${category.slug}`}
        type="website"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main>
        <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
          <ol className="mx-auto flex max-w-site flex-wrap items-center gap-2 px-4 py-3 text-xs text-foreground-600 md:px-6">
            <li>
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
            </li>
            <li aria-hidden="true" className="text-foreground-400">/</li>
            <li className="font-medium text-foreground-900">{category.name}</li>
          </ol>
        </nav>

        <header className="border-b border-background-200 bg-background-50">
          <div className="mx-auto flex max-w-site flex-col gap-5 px-4 py-10 md:flex-row md:items-center md:px-6 md:py-14">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className={`${category.icon} text-3xl`} aria-hidden="true"></i>
            </span>
            <div className="min-w-0">
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                {category.name}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {category.desc} 本栏目共收录 {list.length} 条相关瓜料，按
                {sort === "latest" ? "时间" : "热度"}
                排序呈现。
              </p>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-site px-4 py-12 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-1 rounded-full bg-background-100 p-1">
                  <button
                    type="button"
                    onClick={() => setSort("latest")}
                    aria-pressed={sort === "latest"}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                      sort === "latest"
                        ? "border border-background-300 bg-background-50 font-medium text-foreground-950"
                        : "border border-transparent text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    <i className="ri-time-line mr-1" aria-hidden="true"></i>
                    最新优先
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("hot")}
                    aria-pressed={sort === "hot"}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                      sort === "hot"
                        ? "border border-background-300 bg-background-50 font-medium text-foreground-950"
                        : "border border-transparent text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    <i className="ri-fire-line mr-1" aria-hidden="true"></i>
                    热度优先
                  </button>
                </div>
                <span className="text-xs text-foreground-500">共 {list.length} 条</span>
              </div>

              {list.length === 0 ? (
                <p className="rounded-lg border border-background-200 bg-background-100 p-8 text-center text-sm text-foreground-600">
                  该栏目正在筹备中，新的瓜料很快上新。
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {list.map((item) => (
                    <ArticleCard key={item.id} article={item} />
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-4">
              <section className="mb-6 rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">切换栏目</h2>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {categories.map((item) => (
                    <li key={item.slug}>
                      <Link
                        to={`/category/${item.slug}`}
                        className={`block rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                          item.slug === category.slug
                            ? "bg-primary-500 font-medium text-background-50"
                            : "bg-background-50 text-foreground-700 hover:bg-background-200"
                        }`}
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
              <HotSidebar />
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}